import { useEffect, useState } from "react";
import { FileText, Loader2, Download } from "lucide-react";
import { api, API, inr } from "../../lib/api";
import { toast } from "sonner";
import { openOrDownloadPdf, openPdfUrl, filenameFromContentDisposition } from "../../lib/pdfDownload";
import PdfViewerModal from "../../lib/PdfViewerModal";

/**
 * iter108 — Merchant view of Commission Invoices.
 * Read-only list of GST invoices the platform has raised on the
 * merchant's own shops. PDF download is the primary action.
 */
export default function MerchantCommissionInvoices() {
    const [rows, setRows] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [pdfModal, setPdfModal] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/merchant/commission-invoices?year=${year}`);
            setRows(data.rows || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
            setRows([]);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);

    const download = async (inv) => {
        // iter134m — native: render inline via PdfViewerModal (pdf.js)
        // which works without any native plugins being cap-synced.
        const filename = `commission-${inv.invoice_number.replace(/\//g, "-")}.pdf`;
        const cap = typeof window !== "undefined" ? window.Capacitor : null;
        if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
            setPdfModal({ endpoint: `/merchant/commission-invoices/${inv.id}/pdf`, filename });
            return;
        }
        try {
            const { data, headers } = await api.get(
                `/merchant/commission-invoices/${inv.id}/pdf`, { responseType: "blob" },
            );
            const ctype = (headers["content-type"] || "").toLowerCase();
            if (!ctype.includes("application/pdf")) {
                const text = await data.text();
                toast.error(text?.slice(0, 200) || "Server did not return a PDF");
                return;
            }
            const blob = new Blob([data], { type: "application/pdf" });
            const fn = filenameFromContentDisposition(headers["content-disposition"], filename);
            await openOrDownloadPdf(blob, fn);
        } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    };

    return (
        <div data-testid="merchant-commission-invoices">
            <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                <FileText size={22} /> Commission Invoices
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
                Tax invoices raised by the platform for commission charged on your shops. Download PDFs for your accounting.
            </p>

            <div className="mt-4">
                <select data-testid="mci-year" className="rrinp" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {[0, 1, 2].map((offset) => {
                        const y = new Date().getFullYear() - offset;
                        return <option key={y} value={y}>{y}</option>;
                    })}
                </select>
            </div>

            <div className="mt-4 rounded-2xl border border-border overflow-hidden">
                {/* Desktop table — hidden on mobile */}
                <table className="w-full text-sm hidden md:table">
                    <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Invoice #</th>
                            <th className="px-3 py-2 text-left">Shop</th>
                            <th className="px-3 py-2 text-left">Period</th>
                            <th className="px-3 py-2 text-right">Commission</th>
                            <th className="px-3 py-2 text-right">GST</th>
                            <th className="px-3 py-2 text-right">Total</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows === null ? (
                            <tr><td colSpan={7} className="text-center py-10"><Loader2 size={18} className="inline animate-spin" /></td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">
                                No commission invoices yet.
                            </td></tr>
                        ) : rows.map((r) => {
                            const gst = (r.cgst_amount || 0) + (r.sgst_amount || 0) + (r.igst_amount || 0);
                            const voided = r.status === "VOIDED";
                            return (
                                <tr key={r.id} data-testid={`mci-row-${r.id}`} className={`border-t border-border ${voided ? "opacity-60 line-through" : ""}`}>
                                    <td className="px-3 py-2 font-semibold">{r.invoice_number}</td>
                                    <td className="px-3 py-2">{r.restaurant_name || "—"}</td>
                                    <td className="px-3 py-2">{r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{inr(r.commission_amount)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{gst > 0 ? inr(gst) : "—"}</td>
                                    <td className="px-3 py-2 text-right font-bold tabular-nums">{inr(r.total_amount)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            data-testid={`mci-pdf-${r.id}`}
                                            onClick={() => download(r)}
                                            title="Download PDF"
                                            className="p-1.5 rounded hover:bg-muted"
                                        ><Download size={14} /></button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {/* Mobile card layout — visible below md */}
                <div className="md:hidden divide-y divide-border">
                    {rows === null ? (
                        <div className="text-center py-10"><Loader2 size={18} className="inline animate-spin" /></div>
                    ) : rows.length === 0 ? (
                        <div className="text-center py-10 text-sm text-muted-foreground px-4">
                            No commission invoices yet.
                        </div>
                    ) : rows.map((r) => {
                        const gst = (r.cgst_amount || 0) + (r.sgst_amount || 0) + (r.igst_amount || 0);
                        const voided = r.status === "VOIDED";
                        return (
                            <div
                                key={r.id}
                                data-testid={`mci-row-${r.id}`}
                                className={`p-3 ${voided ? "opacity-60 line-through" : ""}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Invoice</p>
                                        <p className="text-sm font-semibold break-all leading-tight">{r.invoice_number}</p>
                                        <p className="mt-1 text-sm text-foreground/80 truncate">{r.restaurant_name || "—"}</p>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">
                                            {r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
                                        <p className="text-base font-bold tabular-nums leading-tight">{inr(r.total_amount)}</p>
                                        {gst > 0 && (
                                            <p className="text-[11px] text-muted-foreground tabular-nums">GST {inr(gst)}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-2">
                                    <span className="text-[11px] text-muted-foreground">
                                        Commission <span className="tabular-nums font-semibold text-foreground">{inr(r.commission_amount)}</span>
                                    </span>
                                    <button
                                        data-testid={`mci-pdf-${r.id}`}
                                        onClick={() => download(r)}
                                        className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold active:scale-95 transition-transform"
                                    >
                                        <Download size={13} /> PDF
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {pdfModal && (
                <PdfViewerModal
                    endpoint={pdfModal.endpoint}
                    filename={pdfModal.filename}
                    onClose={() => setPdfModal(null)}
                />
            )}
        </div>
    );
}
